import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useGetUserById } from "../../../api/userDetails";
import TailwindSideModal from "../../../components/commonModal/TailwindSideModal";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import { Badge } from "../../../components/ui/common/badge";
import CommonSplitContainer from "../../../components/ui/layout/CommonSplitContainer/CommonSplitContainer";
import { getCompanyName } from "../../../utils/cacheStorage";
import Field from "../shared/components/Field";
import UserAvatar from "../shared/components/UserAvatar";
import UserFormLayout, { UserSection } from "../shared/components/UserFormLayout";
import { UserRoleOption } from "../shared/types/user.types";

interface UserDetailsModalProps {
  visibleModal: boolean;
  id: string;
  roles: UserRoleOption[];
  setVisibleModal: () => void;
  setVisibleEdit: () => void;
}

const formatStatus = (status?: string) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : "";

/**
 * Tailwind rebuild of the legacy `UserDetailsModal`
 * (src/screens/user/userDetails/UserDetailsModal.tsx), following
 * `CompanyDetailsModal`: labelled read-only `Field`s ("—" for empty) instead
 * of disabled inputs, and an "Edit user" shortcut. The user is loaded through
 * the same `useGetUserById` mutation each time the drawer opens. The role
 * shows as the user's own role(s) rather than a disabled checkbox per
 * available role.
 */
const UserDetailsModal = ({
  visibleModal,
  id,
  roles,
  setVisibleModal,
  setVisibleEdit
}: UserDetailsModalProps) => {
  const { data: user, isLoading, mutate: loadUser, reset: resetUser } = useGetUserById();

  useEffect(() => {
    if (visibleModal && id) {
      loadUser(id);
    }
    return () => resetUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const userRoles: string[] = (user?.userRole ?? []).map(
    (name: string) => roles.find(role => role.value === name)?.text ?? name
  );

  return (
    <TailwindSideModal
      open={visibleModal}
      onOpenChange={open => {
        if (!open) setVisibleModal();
      }}
      eyebrow="Users"
      title={fullName || "User details"}
      titleClassName="tw-font-display"
    >
      <CommonSplitContainer
        currentHeight={0.88}
        overflowX="hidden"
        topChild={
          isLoading ? (
            <div className="tw-flex tw-items-center tw-justify-center tw-py-24">
              <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-primary" />
            </div>
          ) : (
            <UserFormLayout>
              <UserSection title="Profile picture">
                <div className="tw-flex tw-items-center tw-gap-5 tw-rounded-xl tw-border tw-border-border tw-bg-muted/30 tw-p-6">
                  <UserAvatar src={user?.profileUrl} alt={fullName} className="tw-h-28 tw-w-28" />
                  <p className="tw-text-base tw-font-medium tw-text-foreground">
                    {fullName || "—"}
                  </p>
                </div>
              </UserSection>

              <UserSection title="User details">
                <div className="tw-grid tw-grid-cols-1 tw-gap-5 sm:tw-grid-cols-2">
                  <Field label="First name" value={user?.firstName} />
                  <Field label="Last name" value={user?.lastName} />
                  <Field label="Email" value={user?.email} />
                  <Field label="Phone" value={user?.phone} />
                  <Field label="Company" value={getCompanyName()} />
                  <Field label="Status" value={formatStatus(user?.status)} />
                </div>
              </UserSection>

              <UserSection title="Access level">
                {userRoles.length ? (
                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    {userRoles.map(name => (
                      <Badge
                        key={name}
                        variant="outline"
                        className="tw-rounded-full tw-px-3 tw-py-1 tw-text-sm tw-font-normal"
                      >
                        {name}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="tw-text-sm tw-text-foreground">—</p>
                )}
              </UserSection>
            </UserFormLayout>
          )
        }
        bottomChild={
          <TailwindBottomButtonView
            cancelButtonStatus
            saveButtonStatus
            cancelButtonTitle="Close"
            saveTitle="Edit user"
            saveDisable={isLoading || !user}
            buttonSize="lg"
            cancelButton={setVisibleModal}
            saveButton={setVisibleEdit}
          />
        }
      />
    </TailwindSideModal>
  );
};

export default UserDetailsModal;
